import { supabase } from '../lib/supabase';
import { connectionQueue } from '../lib/connectionQueue';
import type { OfficeInvite, OfficeNotification, TeamActivityStats } from '../types/office';
import { logCriticalError } from '../lib/devLog';

export async function createOfficeInvite(officeId: string, email: string) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase.rpc('create_office_invite', {
      office_id_param: officeId,
      email_param: email.toLowerCase().trim(),
    });

    if (error) throw error;

    if (!data.success) {
      throw new Error(data.error || 'Failed to create invitation');
    }

    // Fire invite email — best-effort, never blocks the invite flow.
    // The server loads all email content from the database; only the inviteId
    // is sent from the client.
    if (data.invite_id) {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;

        if (token) {
          fetch('/api/send-office-invite-email', {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ inviteId: data.invite_id }),
          }).catch(() => {/* intentionally ignored */});
        }
      } catch {/* intentionally ignored */}
    }

    return data;
  } catch (error) {
    logCriticalError('Error creating office invite', error);
    throw error;
  }
}

export async function acceptOfficeInvite(inviteId: string) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase.rpc('accept_office_invite', {
      invite_id_param: inviteId,
    });

    if (error) throw error;

    if (!data || !data.success) {
      throw new Error(data?.error || 'Failed to accept invitation');
    }

    return data;
  } catch (error) {
    logCriticalError('Error accepting office invite', error);
    throw error;
  }
}

export async function declineOfficeInvite(inviteId: string) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase.rpc('decline_office_invite', {
      invite_uuid: inviteId,
    });

    if (error) throw error;

    if (!data.success) {
      throw new Error(data.error || 'Failed to decline invitation');
    }

    return data;
  } catch (error) {
    logCriticalError('Error declining office invite', error);
    throw error;
  }
}

export async function updateMemberRole(memberId: string, newRole: 'admin' | 'member') {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase.rpc('update_office_member_role', {
      member_uuid: memberId,
      new_role: newRole,
    });

    if (error) throw error;

    if (!data.success) {
      throw new Error(data.error || 'Failed to update member role');
    }

    return data;
  } catch (error) {
    logCriticalError('Error updating member role', error);
    throw error;
  }
}

export async function getOfficeInvites(officeId: string): Promise<OfficeInvite[]> {
  try {
    // Use SECURITY DEFINER function to bypass RLS issues
    const { data, error } = await supabase
      .rpc('get_office_invites_for_admin', {
        office_uuid: officeId
      });

    if (error) throw error;
    return data || [];
  } catch (error) {
    logCriticalError('Error getting office invites', error);
    throw error;
  }
}

export async function getPendingOfficeInvites() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !user.email) {
      return [];
    }

    const { data, error } = await supabase
      .from('office_invites')
      .select(`
        *,
        office:office_id (
          id,
          name,
          billing_organization_id
        ),
        inviter_profile:profiles!office_invites_inviter_id_fkey (
          first_name,
          last_name,
          email
        )
      `)
      .eq('invitee_email', user.email.toLowerCase())
      .eq('status', 'pending')
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });

    if (error) {
      logCriticalError('Error fetching pending office invites', error);
      return [];
    }

    // Enrich with billing organization info if applicable
    const enrichedData = await Promise.all(
      (data || []).map(async (invite) => {
        if (invite.office?.billing_organization_id) {
          try {
            // Use SECURITY DEFINER function to fetch org details (bypasses RLS)
            const { data: orgDetails, error: orgError } = await supabase
              .rpc('get_org_details_for_office_invite', {
                invite_id: invite.id
              });

            if (!orgError && orgDetails && orgDetails.length > 0) {
              const orgData = orgDetails[0];
              return {
                ...invite,
                billing_organization: {
                  id: orgData.organization_id,
                  name: orgData.organization_name,
                  subscription_status: orgData.subscription_status,
                  seat_limit: orgData.seat_limit,
                  seats_used: orgData.seats_used,
                },
              };
            } else if (orgError) {
              logCriticalError('Error fetching org details for invite:', orgError);
            }
          } catch (err) {
            logCriticalError('Exception fetching org details:', err);
          }
        }
        return invite;
      })
    );

    return enrichedData;
  } catch (error) {
    logCriticalError('Error getting pending office invites', error);
    return [];
  }
}

export async function getUserNotifications(): Promise<OfficeNotification[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('office_notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    logCriticalError('Error getting notifications', error);
    return [];
  }
}

export async function markNotificationAsRead(notificationId: string) {
  try {
    const { error } = await supabase
      .from('office_notifications')
      .update({ read: true })
      .eq('id', notificationId);

    if (error) throw error;
  } catch (error) {
    logCriticalError('Error marking notification as read', error);
    throw error;
  }
}

export async function deleteNotification(notificationId: string) {
  try {
    // If this is an office_invite notification, decline the linked invite first
    // so the invite row is resolved and the admin can re-invite the same person.
    const { data: notification } = await supabase
      .from('office_notifications')
      .select('type, data')
      .eq('id', notificationId)
      .maybeSingle();

    if (notification?.type === 'office_invite') {
      const inviteId = notification.data?.invite_id as string | undefined;
      if (inviteId) {
        await declineOfficeInvite(inviteId).catch(() => {
          // Invite may already be resolved (expired/accepted); ignore the error.
        });
      }
    }

    const { error } = await supabase
      .from('office_notifications')
      .delete()
      .eq('id', notificationId);

    if (error) throw error;
  } catch (error) {
    logCriticalError('Error deleting notification', error);
    throw error;
  }
}

export async function getUnreadNotificationCount(): Promise<number> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 0;

    const { count, error } = await supabase
      .from('office_notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('read', false);

    if (error) throw error;
    return count || 0;
  } catch (error) {
    logCriticalError('Error getting unread notification count', error);
    return 0;
  }
}

export async function getNotificationCounts(): Promise<{ office: number; transfer: number; total: number }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { office: 0, transfer: 0, total: 0 };
    }

    // Use consolidated RPC function with connection queue - 1 query instead of 2!
    const { data, error } = await connectionQueue.enqueue(() =>
      supabase.rpc('get_notification_counts')
    );

    if (error) throw error;

    const officeCount = data?.office_invite_count || 0;
    const transferCount = data?.transfer_request_count || 0;

    return {
      office: officeCount,
      transfer: transferCount,
      total: officeCount + transferCount
    };
  } catch (error) {
    logCriticalError('Error getting notification counts', error);
    return { office: 0, transfer: 0, total: 0 };
  }
}

export async function getOfficeActivityStats(officeId: string): Promise<TeamActivityStats[]> {
  try {
    const { data, error } = await supabase
      .rpc('get_office_activity_stats', { office_uuid: officeId });

    if (error) throw error;
    return (data || []) as TeamActivityStats[];
  } catch (error) {
    logCriticalError('Error fetching office activity stats', error);
    throw error;
  }
}

export async function cancelOfficeInvite(inviteId: string) {
  try {
    const { error } = await supabase
      .from('office_invites')
      .update({ status: 'expired' })
      .eq('id', inviteId);

    if (error) throw error;
  } catch (error) {
    logCriticalError('Error canceling office invite', error);
    throw error;
  }
}
